import {useQueryClient} from "@tanstack/react-query";
import {useCommonItems} from "../../../../../../contexts/CommonItensProvider";
import {
    Box, Button, CircularProgress,
    Dialog, DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle, Divider, FormControl,
    Step,
    StepButton, StepLabel,
    Stepper, Tooltip, Typography,
    useMediaQuery
} from "@mui/material";
import {useAppContext} from "../../../../../../contexts/AppContext";
import {useEffect, useState} from "react";
import {formulaCalRotationTimes, getDefaultValueSelectField} from "../../../../../../utils";
import {useForm, useWatch} from "react-hook-form";
import {carregaConfigsEquipamento, manipulaEquipamento} from "../../configs/functions";
import {fillInputErrors} from "../../../../../../configs/functions";
import {FirstStepMachineForm} from "./FirstStep";
import {SecondStepMachineForm} from "./SecondStep";
import {ThirdStepMachineForm} from "./ThirdStep";
import {CopyAll} from "@mui/icons-material";
import {CopyEquipmentSettingsDialog} from "../CopyEquipmentSettingsDialog";
import {normalizeMotoresFromBackend} from "./config/functions";

export function MachineForm({ open, setOpen, maquina, empresas, queryKey, readOnly }) {
    const queryClient = useQueryClient();
    const { exibirDialog, handleCloseDialog, exibirAlerta } = useCommonItems();
    const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));

    const { dadosUsuario } = useAppContext();

    const isEditAction = !!maquina;
    const [activeStep, setActiveStep] = useState(0);
    const defaultRotationTimeValues = Array.from({ length: 256 }, (_, index) => formulaCalRotationTimes(index));

    const createArrayMotores = qtdMotores => {
        return Array.from({ length: qtdMotores }, (_, index) => ({
            id: "",
            id_equipamento: maquina ? maquina.id : "",
            id_produto: "",
            id_produto_pai: "",
            mustSelectChild: false,
            numero_motor: index + 1,
            status: 'livre',
            tempo_rotacao: defaultRotationTimeValues[defaultRotationTimeValues.length / 2],
            multiplicador: 1,
            numero_rotacoes: 1,
            agrupado_por: null,
            qtd_produto: 0,
            capacidade: 1,
            is_dirty: false,
        }));
    };

    const [qtdMotores, setQtdMotores] = useState(36);
    const [motores, setMotores] = useState(createArrayMotores(36));
    const [isLoading, setIsLoading] = useState(false);

    const [copiarOpen, setCopiarOpen] = useState(false);

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        control,
        formState: { dirtyFields, errors },
        trigger,
        setError,
        clearErrors,
        reset,
    } = useForm();

    const idEmpresa = useWatch({ control, name: 'id_empresa' });
    const nomeWatch = useWatch({ control, name: 'nome' });
    const tagWatch = useWatch({ control, name: 'tag_identificacao' });
    const pontoEntregaWatch = useWatch({ control, name: 'id_ponto_entrega' });
    const descricaoWatch = useWatch({ control, name: 'descricao' });

    const empresaSelecionada = !!idEmpresa;
    const nomePreenchido = !!nomeWatch?.trim();
    const podeCopiar = dadosUsuario.isSempher && activeStep === 0;

    const pontoEntregaPreenchido = !!pontoEntregaWatch;

    const copiaHabilitada = empresaSelecionada
        && (isEditAction || (nomePreenchido && pontoEntregaPreenchido));

    const tooltipCopia = !empresaSelecionada
        ? 'Selecione uma Empresa antes de copiar as configurações'
        : (!isEditAction && !nomePreenchido)
            ? 'Informe o Nome da máquina antes de copiar as configurações'
            : (!isEditAction && !pontoEntregaPreenchido)
                ? 'Selecione o Ponto de Entrega antes de copiar as configurações'
                : 'Copiar as configurações de motores de outra máquina ou de uma versão anterior';

    const onSubmit = async (data) => {
        if (readOnly) return;

        let formDataToSend = {};
        exibirDialog('Salvando. Aguarde...');

        if (isEditAction) {
            const dirtyFieldKeys = Object.keys(dirtyFields);

            const dirtyData = dirtyFieldKeys.reduce((acc, key) => {
                if (data.hasOwnProperty(key)) {
                    acc[key] = data[key];
                }
                return acc;
            }, {});

            if (Object.keys(dirtyData).length === 0 && !motores.some(motor => motor.is_dirty)) {
                exibirAlerta('Atenção', 'Nenhum dado foi alterado.', 'warning');
                setOpen(false);
                handleCloseDialog();
                return;
            }

            formDataToSend = dirtyData;
            formDataToSend.id = maquina.id;
        } else {
            delete data.id;

            if (!data.tag_identificacao) {
                delete data.tag_identificacao;
            }

            formDataToSend = data;
        }

        const motorComProdutoPaiSemVariacao = motores.find(motor => !motor.id_produto && motor.mustSelectChild);

        if (motorComProdutoPaiSemVariacao) {
            exibirAlerta('Ops', 'Selecione a variação do produto para o motor ' + motorComProdutoPaiSemVariacao.numero_motor, 'error');
            handleCloseDialog();
            return;
        } else {
            // remover prop para não enviar ao backend
            motores.forEach(motor => {
                delete motor.id_produto_pai;
                delete motor.mustSelectChild;
            });
        }

        formDataToSend.configsEquipamento =
            motores
                .map((motor) => {
                    const { id_produto_pai, mustSelectChild, ...rest} = motor;

                    return { ...rest, is_dirty: !!motor.is_dirty};
                })
                .sort((a, b) => {
                    const aChild = a.agrupado_por ? 1 : 0;
                    const bChild = b.agrupado_por ? 1 : 0;

                    return aChild - bChild;
                });


        try {
            const res = await manipulaEquipamento(formDataToSend, isEditAction);
            exibirAlerta('Sucesso', res.data.message, 'success');
            setOpen(false);

            queryClient.invalidateQueries({ queryKey });
        } catch (error) {
            let errorMessage = "Erro ao cadastrar Equipamento.";
            if (error.response) {
                errorMessage = error.response.data.message
                fillInputErrors(error, setError);
            }
            exibirAlerta('Erro', errorMessage, 'error');
        } finally {
            handleCloseDialog();
        }
    };

    const validaCamposStep01 = async () => {
        const isValidNome = await trigger('nome');
        const isValidTagIdentificacao = await trigger('tag_identificacao');
        const isValidDescricao = await trigger('descricao');
        const isValidTipoContrato = await trigger('tipo_contrato');
        const isValidEmpresa = await trigger('id_empresa');
        const isValidPontoEntrega = await trigger('id_ponto_entrega');

        const state =
            isValidNome &&
            isValidDescricao &&
            isValidTagIdentificacao &&
            isValidEmpresa &&
            isValidPontoEntrega &&
            isValidTipoContrato;

        return state;
    };

    const handleNext = async (event) => {
        event.preventDefault();
        switch (activeStep) {
            case 0:
                if (await validaCamposStep01()) {
                    setActiveStep((prevActiveStep) => prevActiveStep + 1);
                }
                break;
            case 1:
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
                break;
            case 2:
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
                break;
            default:
                break;
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStep = (pos) => {
        if (pos < activeStep) {
            setActiveStep(pos)
        }
    }

    const steps =  ['Dados do Equipamento', 'Organização da Máquina', 'Configurações dos Motores'];

    const loadConfigsEquipamento = async () => {
        try {
            setIsLoading(true);

            const filter = { id_equipamento: maquina.id };
            const response = await carregaConfigsEquipamento(filter);
            const motoresNormalizados = normalizeMotoresFromBackend(response?.data?.data ?? []);

            setMotores(motoresNormalizados);

        } catch (error) {
            exibirAlerta('Erro ao carregar as Configurações do Equipamento', '', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setActiveStep(0);

        // preenche o formulário ao abrir p/ edicao
        if (open) {
            const defaultValueIdEmpresa = getDefaultValueSelectField(maquina, maquina?.empresa?.id, empresas);

            const defaultValues = {
                id: maquina ? maquina.id : '',
                nome: maquina ? maquina.nome : '',
                tag_identificacao: maquina ? maquina.tag_identificacao : '',
                status: maquina ? maquina.status : 1,
                tipo: maquina ? maquina.tipo : 'maquina',
                tipo_contrato: maquina ? maquina.tipo_contrato : 'locacao',
                descricao: maquina ? maquina.descricao : '',
                id_empresa: defaultValueIdEmpresa,
                id_ponto_entrega: maquina ? maquina.pontoEntrega.id : '',
            };

            reset(defaultValues);
            setQtdMotores(maquina ? maquina.qtd_motores : 36);

            // get the configEquipamento from the equipamento
            if (maquina) {
                loadConfigsEquipamento();
            }
        } else {
            reset();
            setMotores([]);
            setQtdMotores(36);
        }
    }, [open, reset]);

    useEffect(() => {
        if (open && !isEditAction) {
            setMotores(createArrayMotores(qtdMotores));
        }
    }, [open, qtdMotores, isEditAction]);

    return (
        <Dialog open={open} fullWidth maxWidth={'lg'} disableEscapeKeyDown>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        }}
                    >
                        <Typography variant="h6" component="div">
                            {dadosUsuario.isSempher
                                ? isEditAction
                                    ? <>Editando Máquina <strong>{maquina.nome}</strong></>
                                    : 'Cadastrar nova Máquina'
                                : <>Visualizando dados de <strong>{maquina ? maquina.nome : ''}</strong></>
                            }
                        </Typography>

                        {podeCopiar && (
                            <Tooltip title={tooltipCopia} arrow>
                                <span>
                                     <Button
                                         variant="outlined"
                                         size="small"
                                         type="button"
                                         startIcon={<CopyAll />}
                                         onClick={() => setCopiarOpen(true)}
                                         disabled={!copiaHabilitada}
                                         sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                     >
                                        Copiar configurações
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        {dadosUsuario.isSempher
                            ? isEditAction ? 'Preencha corretamente os dados.' : 'Preencha corretamente os dados abaixo para cadastrar um novo Equipamento.'
                            : ''
                        }
                    </DialogContentText>

                    <Stepper
                        activeStep={activeStep}
                        sx={{
                            px: { xs: 2, sm: 10 },
                            py: 2,
                            overflowX: 'auto',
                            '&::-webkit-scrollbar': { display: 'none' },
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none',
                        }}
                        orientation={isMobile ? 'vertical' : 'horizontal'}
                    >
                        {steps.map((label, index) => (
                            <Step key={label}>
                                <StepButton color="inherit" onClick={() => handleStep(index)}>
                                    <StepLabel>{label}</StepLabel>
                                </StepButton>
                            </Step>
                        ))}
                    </Stepper>

                    <Box sx={{ p: 2, backgroundColor: '#fafafa' }} >
                        <FormControl variant="standard" fullWidth>
                            {activeStep === 0 &&
                                <FirstStepMachineForm
                                    values={getValues()}
                                    maquina={maquina}
                                    empresas={empresas}
                                    register={register}
                                    errors={errors}
                                    clearErrors={clearErrors}
                                    control={control}
                                    setQtdMotores={setQtdMotores}
                                    setValue={setValue}
                                />
                            }
                            {activeStep === 1 &&
                                <SecondStepMachineForm
                                    motores={motores}
                                    setMotores={setMotores}
                                    qtdMotores={qtdMotores}
                                />
                            }
                            {activeStep === 2 &&
                                <ThirdStepMachineForm
                                    values={getValues()}
                                    equipamento={maquina}
                                    motores={motores}
                                    setMotores={setMotores}
                                    qtdMotores={qtdMotores}
                                />
                            }
                        </FormControl>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)} variant="contained">
                        Cancelar
                    </Button>

                    <Divider orientation="vertical" flexItem />

                    {activeStep !== 0 && (
                        <Button onClick={handleBack} variant="outlined" type="button">
                            Voltar
                        </Button>
                    )}
                    {activeStep !== steps.length - 1 ? (
                        <Button onClick={handleNext} variant="contained" type="button" disabled={isLoading}>
                            {!isLoading ? 'Próximo' : <><CircularProgress size={16} sx={{ marginRight: 1 }} />Carregando</>}
                        </Button>
                    ) : (
                        !readOnly && (
                            <Button type="submit" variant="contained">Pronto</Button>
                        )
                    )}
                </DialogActions>
            </form>

            <CopyEquipmentSettingsDialog
                open={copiarOpen}
                onClose={() => setCopiarOpen(false)}
                tipo="maquina"
                idEmpresa={idEmpresa}
                equipamento={maquina}
                dadosCriacao={{
                    nome: nomeWatch,
                    tag_identificacao: tagWatch,
                    id_ponto_entrega: pontoEntregaWatch,
                    descricao: descricaoWatch,
                }}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey });
                    setOpen(false);
                }}
            />
        </Dialog>
    );
}